import { usePagination } from "@ajna/pagination"
import {
	Box,
	Button,
	Flex,
	Input,
	InputGroup,
	InputRightAddon,
	Tag,
	Text,
	useDisclosure,
} from "@chakra-ui/react"
import { FC, useCallback, useEffect, useRef, useState } from "react"
import { BsSearch } from "react-icons/bs"
import ReactSelect from "react-select"
import { CustomPagination } from "src/components/shared/CustomPagination"
import { InputLabel } from "src/components/ui/InputLabel"
import { EducationCategory } from "src/domain/entities/educationCategory"
import { fetchGujaratiSuggestions } from "src/utils/helpers"

import { useEducationCategoryListApi } from "../../../domain/hooks"
import { DashboardWrapper } from "../../wrappers/DashboardWrapper"
import { EducationCategoryAddDrawerFormController } from "./EducationCategoryAddDrawerForm"
import { EducationCategoryDeleteDialogController } from "./EducationCategoryDeleteDialogController"
import { EducationCategoryListController } from "./EducationCategoryList"
import { EducationCategoryUpdateDrawerFormController } from "./EducationCategoryUpdateDrawerForm"

export const EducationCategoriesPage: FC = () => {
	const { educationCategoryList, isLoading, fetchEducationCategoryList } =
		useEducationCategoryListApi()
	const {
		educationCategoryList: parentEducationCategoryList,
		isLoading: isParentEducationCategoryListLoading,
		fetchEducationCategoryList: fetchParentEducationCategoryList,
	} = useEducationCategoryListApi()
	const [isEducationCategoryDeleteDialogOpen, setIsEducationCategoryDeleteDialogOpen] =
		useState(false)
	const educationCategoryAddDrawerDisclosure = useDisclosure()
	const educationCategoryUpdateDrawerDisclosure = useDisclosure()

	const [selectedParentEducationCategory, setSelectedParentEducationCategory] =
		useState<EducationCategory>()

	const pagination = usePagination({
		initialState: { currentPage: 1, pageSize: 15 },
	})
	const [searchText, setSearchText] = useState("")
	const [gujaratiSuggestions, setGujaratiSuggestions] = useState<string[]>([])

	const getGujaratiSuggestions = useCallback(async (searchText: string) => {
		const suggestions = await fetchGujaratiSuggestions(searchText)
		setGujaratiSuggestions(suggestions)
	}, [])

	const fetchEducationCategories = useCallback(async () => {
		return await fetchEducationCategoryList({
			fetch: { childCategories: true },
			search: searchText,
			parentCategoryId: null,
			pagination: {
				page: pagination.currentPage,
				limit: 15,
			},
		})
	}, [fetchEducationCategoryList, pagination.currentPage, searchText])

	useEffect(() => {
		fetchEducationCategories()
	}, [fetchEducationCategories])

	useEffect(() => {
		fetchParentEducationCategoryList({
			fetch: { childCategories: true },
		})
	}, [fetchParentEducationCategoryList])

	const updateEducationCategoryRef = useRef<EducationCategory>()
	const deleteEducationCategoryRef = useRef<EducationCategory>()

	const parentEducationCategoryOptions: {
		label: string
		value?: EducationCategory
	}[] = parentEducationCategoryList.map((parentEducationCategory) => ({
		label: parentEducationCategory.name.en,
		value: parentEducationCategory,
	}))

	parentEducationCategoryOptions.unshift({
		label: "All",
		value: undefined,
	})

	return (
		<DashboardWrapper>
			<Box padding={2}>
				<Flex justifyContent="space-between" alignItems="center">
					<Text
						fontSize="2xl"
						fontWeight="bold"
						display={{ base: "none", lg: "contents" }}
					>
						Education Categories
					</Text>
					<Button
						display={{ base: "none", lg: "block" }}
						size="sm"
						onClick={educationCategoryAddDrawerDisclosure.onOpen}
						colorScheme="blue"
						m={{ base: "auto", lg: "0" }}
					>
						Add Education Category
					</Button>
				</Flex>
			</Box>
			<Box px={2} mb={4}>
				<Flex
					gridColumnGap={2}
					align="flex-end"
					direction={{ base: "column", lg: "row" }}
				>
					<Box width={{ base: "full", lg: "fit-content" }}>
						<InputGroup>
							<Input
								value={searchText}
								onChange={(e) => {
									pagination.setCurrentPage(1)
									setSearchText(e.target.value)
									getGujaratiSuggestions(e.target.value)
								}}
								type="text"
								placeholder="Search"
							/>
							<InputRightAddon>
								<BsSearch />
							</InputRightAddon>
						</InputGroup>
						{gujaratiSuggestions.map((el, i) => (
							<Tag
								key={i}
								colorScheme={"green"}
								backgroundColor={"green.50"}
								variant="outline"
								_hover={{
									backgroundColor: "green.100",
								}}
								cursor="pointer"
								margin={0.5}
								onClick={() => {
									pagination.setCurrentPage(1)
									setSearchText(el)
									setGujaratiSuggestions([])
								}}
							>
								{el}
							</Tag>
						))}
					</Box>
					{/* Parent Category */}
					<Box width={{ base: "full", lg: "20%" }} my={{ base: "2", lg: "0" }}>
						<InputLabel label="Parent Category" />
						<ReactSelect
							name="parentEducationCategoryId"
							onChange={(val) => {
								pagination.setCurrentPage(1)
								setSelectedParentEducationCategory(val?.value)
							}}
							isLoading={isParentEducationCategoryListLoading}
							value={parentEducationCategoryOptions.find(
								(el) =>
									el.value?.id === selectedParentEducationCategory?.id,
							)}
							options={parentEducationCategoryOptions}
						/>
					</Box>
					<Button
						display={{ base: "block", lg: "none" }}
						size="sm"
						onClick={educationCategoryAddDrawerDisclosure.onOpen}
						colorScheme="blue"
						m={{ base: "auto", lg: "0" }}
					>
						Add Education Category
					</Button>
				</Flex>
			</Box>
			<EducationCategoryListController
				list={educationCategoryList}
				isLoading={isLoading}
				onUpdate={(educationCategory: EducationCategory) => {
					updateEducationCategoryRef.current = educationCategory
					educationCategoryUpdateDrawerDisclosure.onOpen()
				}}
				onDelete={(educationCategory: EducationCategory) => {
					deleteEducationCategoryRef.current = educationCategory
					setIsEducationCategoryDeleteDialogOpen(true)
				}}
				onAddClick={educationCategoryAddDrawerDisclosure.onOpen}
			/>
			<CustomPagination
				pagination={pagination}
				isNextDisabled={educationCategoryList.length === 0}
			/>
			{educationCategoryAddDrawerDisclosure.isOpen ? (
				<EducationCategoryAddDrawerFormController
					{...educationCategoryAddDrawerDisclosure}
					onSuccess={() => fetchEducationCategories()}
				/>
			) : null}
			{updateEducationCategoryRef.current &&
			educationCategoryUpdateDrawerDisclosure.isOpen ? (
				<EducationCategoryUpdateDrawerFormController
					{...educationCategoryUpdateDrawerDisclosure}
					educationCategory={updateEducationCategoryRef.current}
					onSuccess={() => fetchEducationCategories()}
				/>
			) : null}
			{deleteEducationCategoryRef.current && isEducationCategoryDeleteDialogOpen ? (
				<EducationCategoryDeleteDialogController
					isOpen={isEducationCategoryDeleteDialogOpen}
					setIsOpen={setIsEducationCategoryDeleteDialogOpen}
					educationCategory={deleteEducationCategoryRef.current}
					onSuccess={() => fetchEducationCategories()}
				/>
			) : null}
		</DashboardWrapper>
	)
}
